
import Modal from "@/components/Modal.vue";
import MultiSelect from "@/components/MultiSelect.vue";
import CustomDateInput from "@/components/CustomDateInput.vue";
import { apiCall } from "../../lib/api";
import { copyObject, formatDate, gettersInstance, logError, propsInstance, getApiaTooltip, getCropLabelWithApia } from "../../lib/util";
import ParcelPlaceholder from "./ParcelPlaceholder.vue";
import { Parcel } from "@/lib/types/Parcel";
import { CropInterval } from "@/lib/types/CropInterval";
import { Component, MixedVue, mixins } from "@/vue";
import { CropDatesEstimate } from "@/lib/types/actions/CropDatesEstimate";
import { Crop } from "@/lib/types/Crop";
import { AgriculturalWork } from "@/lib/types/AgriculturalWork";

const StoreGetters = gettersInstance(["selectedParcelId", "selectedParcel", "allCropGroups", "seasons", "currentFarmId", "currentSeasonId", "parcelIndex"]);

interface ChangeParcelCropProps {
  parcelProp?: Parcel;
  cropIntervalProp?: CropInterval;
  focusMeasuredYield?: boolean;
}

const Props = propsInstance<ChangeParcelCropProps>({
  parcelProp: {
    type: Object,
    required: false,
    default: null,
  },
  cropIntervalProp: {
    type: Object,
    required: false,
    default: null,
  },
  focusMeasuredYield: {
    type: Boolean,
    required: false,
    default: false,
  },
});

@Component({
  name: "ChangeParcelCrop",
  components: {
    Modal,
    MultiSelect,
    ParcelPlaceholder,
    CustomDateInput,
  },
})
export default class ChangeParcelCrop extends mixins(MixedVue, Props, StoreGetters) {
  static components: {
    Modal: typeof Modal;
    MultiSelect: typeof MultiSelect;
    ParcelPlaceholder: typeof ParcelPlaceholder;
    CustomDateInput: typeof CustomDateInput;
  };

  $refs: {
    modal: Modal;
    measuredInput: HTMLInputElement;
  };

  cropInterval: CropInterval = {
    id: null,
    crop_id: null,
    interval: {
      lower: null,
      upper: null,
      bounds: "[)",
    },
    yield_forecast: null,
    yield_forecasts: null,
    yield_estimated: null,
    yield_measured: null,
    crop_hybrid: null,
    observations: null,
    density: null,
    density_unit: "kg_per_ha",
    agricultural_work: null,
  };
  initialCropInterval: CropInterval = null;
  loadingRequest: boolean = false;
  parcel: Parcel = null;
  step: 1 | 2 = 1;

  created() {
    this.parcel = this.parcelProp || this.selectedParcel;
    this.initialCropInterval = this.cropIntervalProp || this.getCropInterval(this.parcel.crop_intervals);
    if (this.initialCropInterval) {
      // Merge onto the default so every field (incl. the newer observations /
      // density / density_unit) is present and reactive even when the loaded
      // interval doesn't carry those keys — otherwise Vue 2 can't track them.
      this.cropInterval = { ...this.cropInterval, ...copyObject(this.initialCropInterval) };
    }
    // Default the density unit so the dropdown is never empty.
    if (!this.cropInterval.density_unit) {
      this.cropInterval.density_unit = "kg_per_ha";
    }
    this.$nextTick(() => {
      if (this.focusMeasuredYield && this.$refs.measuredInput) {
        this.$refs.measuredInput.focus();
        this.$refs.measuredInput.select?.();
      }
    });
  }

  get crops() {
    const result = Object.values(this.allCropGroups).map((crop) => {
      const apiaLink = crop.system_links?.find((link) => link.name === "APIA");
      const apiaCode = apiaLink?.crop_code || null;
      return {
        id: [...crop.cropIds][0],
        name: crop.name[this.$language.current],
        // Make a separate group for the unspecified crop
        group: crop.id == "null" ? this.$gettext("Unspecified crop") : "",
        system_links: crop.system_links,
        apia_code: apiaCode,
        title: getApiaTooltip(apiaCode),
      };
    });
    result.slice(0, 5).forEach((crop) => {
      crop.group = this.$gettext("Frequently used");
    });
    // Take the noCropObject from the end of the array and put it at the beginning
    let noCropObject = result.pop();
    result.splice(0, 0, noCropObject);
    return result;
  }
  getCropLabelWithApia = getCropLabelWithApia;

  get agriculturalWorks() {
    const agriculturalWorks = [{ id: null, name: this.$gettext("Unspecified") }, ...this.$store.getters.agriculturalWorks];
    return agriculturalWorks;
  }
  get selectedAgriculturalWork() {
    return this.cropInterval.agricultural_work;
  }

  get headerTitle() {
    return this.step === 1 ? this.$gettext("Set crop") : this.$gettext("More details");
  }

  get detailsNavLabel() {
    return this.step === 1 ? this.$gettext("More details") : this.$gettext("Back");
  }

  get densityUnitOptions() {
    return [
      { value: "seeds_per_m2", label: this.$gettext("seeds/m²") },
      { value: "kg_per_ha", label: this.$gettext("kg/ha") },
      { value: "plants_per_ha", label: this.$gettext("plants/ha") },
    ];
  }

  get densityNeedsUnit() {
    const density = this.cropInterval.density;
    return density != null && (density as unknown as string) !== "" && !this.cropInterval.density_unit;
  }

  get detailChips() {
    const ci = this.cropInterval;
    const chips: string[] = [];
    if (ci.density != null && (ci.density as unknown as string) !== "") {
      const unitLabel = this.densityUnitOptions.find((u) => u.value === ci.density_unit)?.label || "";
      chips.push(`${this.$gettext("Density")}: ${ci.density} ${unitLabel}`.trim());
    }
    if (ci.agricultural_work) chips.push(`${this.$gettext("Work type")}: ${ci.agricultural_work}`);
    if (ci.observations) {
      const note = ci.observations.length > 30 ? `${ci.observations.slice(0, 30)}…` : ci.observations;
      chips.push(`${this.$gettext("Observations")}: ${note}`);
    }
    return chips;
  }

  get parcelSeason() {
    return this.seasons[this.parcel.season];
  }

  get parcelSeasonInterval() {
    return this.parcelSeason?.interval;
  }

  get selectedCrop() {
    return this.cropInterval?.crop_id;
  }

  get sowDate() {
    return this.cropInterval?.interval.lower;
  }

  get harvestDate() {
    return this.cropInterval?.interval.upper;
  }

  get hasForecastAndDeleting() {
    if (this.cropInterval.crop_id != null && this.cropInterval.crop_id !== "null") return false;
    if (!this.initialCropInterval?.id || this.initialCropInterval.id === "null") return false;
    const forecasts = this.initialCropInterval.yield_forecasts;
    return Array.isArray(forecasts) && forecasts.length > 0;
  }

  get formValid() {
    return this.selectedCrop && !this.densityNeedsUnit;
  }

  get formChanged() {
    return (
      this.initialCropInterval?.crop_id != this.cropInterval.crop_id ||
      this.initialCropInterval?.interval.lower != this.cropInterval.interval.lower ||
      this.initialCropInterval?.interval.upper != this.cropInterval.interval.upper
    );
  }

  get minSowDate() {
    return formatDate(this.parcelSeason.start_date);
  }

  get maxSowDate() {
    const seasonEndDate = formatDate(this.parcelSeason.end_date);
    return this.getDateMinMax(seasonEndDate, this.harvestDate, false);
  }

  get minHarvestDate() {
    const seasonStartDate = formatDate(this.parcelSeason.start_date);
    return this.getDateMinMax(seasonStartDate, this.sowDate, false);
  }

  get maxHarvestDate() {
    const seasonEndDate = formatDate(this.parcelSeason.end_date);
    return this.getDateMinMax(seasonEndDate, this.sowDate, false);
  }

  async onWorkTypeSelect(selectedWorkType: AgriculturalWork) {
    this.cropInterval.agricultural_work = selectedWorkType.id !== null ? selectedWorkType.name : null;
  }

  onDensityUnitSelect(unit: { value: CropInterval["density_unit"] } | null) {
    this.cropInterval.density_unit = unit?.value ?? null;
  }

  async onCropSelect(crop: Crop) {
    this.cropInterval.crop_id = crop.id;
    if (crop.id == "null") {
      this.cropInterval.interval.lower = null;
      this.cropInterval.interval.upper = null;
      return;
    }
    const apiaLink = crop.system_links?.find((link) => link?.name === "APIA")?.name?.toLowerCase();

    if (!apiaLink) return;
    const estimatedData = await apiCall<CropDatesEstimate>(
      "GET",
      `/farm/${this.currentFarmId}/season/${this.parcel.season}/parcel/${this.parcel.id}/crop_dates_estimate/${apiaLink}/${crop.id}/`
    );
    this.cropInterval.interval.lower = estimatedData.start_date;
    this.cropInterval.interval.upper = estimatedData.end_date;
  }

  async saveChanges() {
    this.loadingRequest = true;
    try {
      const cropInterval = {
        ...this.cropInterval,
        crop: this.cropInterval.crop_id,
        yield_estimated: this.cropInterval.yield_estimated || null,
        yield_measured: this.cropInterval.yield_measured || null,
        interval: {
          lower: this.cropInterval.interval.lower,
          upper: this.cropInterval.interval.upper,
          bounds: "[)",
        },
        crop_hybrid: this.cropInterval.crop_hybrid || "",
        observations: this.cropInterval.observations || "",
        density: this.cropInterval.density || null,
        density_unit: this.cropInterval.density_unit || null,
        agricultural_work: this.cropInterval.agricultural_work,
      };

      if (this.cropInterval.id != null && this.cropInterval.id !== "null") {
        const resourceUrl = `/farm/${this.currentFarmId}/season/${this.parcel.season}/parcel/${this.parcel.id}/cropinterval/${this.cropInterval.id}/`;
        if (this.cropInterval.crop_id === "null") {
          await apiCall("DELETE", resourceUrl);
        } else {
          await apiCall("PUT", resourceUrl, {}, cropInterval);
        }
      } else if (this.cropInterval.crop_id !== "null") {
        await apiCall("POST", `/farm/${this.currentFarmId}/season/${this.parcel.season}/parcel/${this.parcel.id}/cropinterval/`, {}, cropInterval);
      }

      this.initialCropInterval = copyObject(this.cropInterval);
      this.$store.commit("clearParcelOverlapCache");

      await this.$store.dispatch("refreshSeasonCrops", this.currentSeasonId);

      const parcels = await this.$store.dispatch("getParcelsForCurrentSeason");

      if (this.parcel.id == this.selectedParcel.id && this.parcel.season == this.currentSeasonId) {
        const updatedParcel = parcels.find((parcel) => parcel.id == this.parcel.id);
        await this.$store.dispatch("getSelectedParcelHistory", this.parcelIndex[this.parcel.id]);
        this.$store.commit("setSelectedParcel", updatedParcel);
      }

      this.$nextTick(() => this.$emit("change"));

      if (this.cropInterval?.yield_measured) {
        this.$nextTick(() => this.$emit("measured-yield-saved"));
      }

      this.notify({
        type: "success",
        icon: "spa",
        text: this.$gettext("Crop set."),
      });
      this.$refs.modal.closeModal();
    } catch (err) {
      logError(err);
      this.notify({
        type: "error",
        text: this.$gettext("Something went wrong."),
      });
      this.loadingRequest = false;
    }
  }

  closeCheck() {
    return !this.formChanged || confirm(this.$gettext("There are unsaved changes. Do you want to continue?"));
  }

  getDateMinMax(date1, date2, min = true) {
    if (!date1 || !date2) return date1 || date2;
    const sorted = [date1, date2].sort();
    return min ? sorted[0] : sorted[1];
  }

  getDatePlaceholder(user, estimated) {
    const date = user || estimated;
    if (!date) return this.$gettext("Select a date...");
    let str = new Date(date).toLocaleDateString(this.$language.current, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    if (!user) str += "*";
    return str;
  }
}
